import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { QueryErrorState } from '@/components/common/QueryErrorState';
import { CardHeaderActions, type ActionItem } from '@/components/common/CardHeaderActions';
import { Plus, Trash2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import type { ScheduleDto } from '@/api/control/missionImpactAPI.schemas';

/**
 * Props for the QuietTimeCard component
 */
interface QuietTimeCardProps {
  /** Whether schedules are currently loading */
  isLoading: boolean;
  /** Error from loading schedules, if any */
  error: Error | null;
  /** List of schedules to display */
  schedules: ScheduleDto[];
  /** Whether the current user can edit schedules */
  canEdit: boolean;
  /** Callback to refetch schedules after an error */
  onRefetch: () => void;
  /** Callback to open the create schedule dialog */
  onCreateSchedule: () => void;
  /** Callback when user wants to delete a schedule */
  onDeleteSchedule: (schedule: ScheduleDto) => void;
}

/**
 * Card component for displaying and managing quiet time schedules
 */
export function QuietTimeCard({
  isLoading,
  error,
  schedules,
  canEdit,
  onRefetch,
  onCreateSchedule,
  onDeleteSchedule,
}: QuietTimeCardProps) {
  const { t } = useLanguage();

  // Days of week array using translations
  const daysOfWeek = [
    t.profileDetail.daysOfWeek.sunday,
    t.profileDetail.daysOfWeek.monday,
    t.profileDetail.daysOfWeek.tuesday,
    t.profileDetail.daysOfWeek.wednesday,
    t.profileDetail.daysOfWeek.thursday,
    t.profileDetail.daysOfWeek.friday,
    t.profileDetail.daysOfWeek.saturday,
  ];

  /**
   * Format time string to display format
   */
  const formatTime = (time: string) => {
    const parts = time.split(':');
    return `${parts[0]}:${parts[1]} UTC`;
  };

  /**
   * Format schedule recurrence to display format
   */
  const formatScheduleRecurrence = (schedule: ScheduleDto) => {
    if (schedule.$type === 'QuietTimeDaily') {
      return t.profileDetail.quietTime.daily;
    } else {
      const dayNames = schedule.days.map((d: number) => daysOfWeek[d]).join(', ');
      return dayNames || t.profileDetail.quietTime.noDaysSelected;
    }
  };

  // Build actions based on edit permissions
  const actions: ActionItem[] = canEdit
    ? [
        {
          key: 'add',
          label: t.profileDetail.quietTime.addQuietTimeBlock,
          icon: Plus,
          onClick: onCreateSchedule,
          tooltip: t.profileDetail.quietTime.addQuietTimeBlock,
        },
      ]
    : [];

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle>{t.profileDetail.quietTime.title}</CardTitle>
            <CardDescription>{t.profileDetail.quietTime.description}</CardDescription>
          </div>
          <CardHeaderActions actions={actions} />
        </div>
      </CardHeader>
      <CardContent>
        {error ? (
          <QueryErrorState
            error={error}
            message={t.errors.profiles.failedToLoadSchedules}
            onRetry={onRefetch}
          />
        ) : isLoading ? (
          <div className="text-muted-foreground">{t.profileDetail.quietTime.loadingSchedules}</div>
        ) : schedules.length === 0 ? (
          <div className="text-muted-foreground">{t.profileDetail.quietTime.noSchedules}</div>
        ) : (
          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t.profileDetail.quietTime.recurrence}</TableHead>
                  <TableHead>{t.profileDetail.quietTime.startTime}</TableHead>
                  <TableHead>{t.profileDetail.quietTime.endTime}</TableHead>
                  {canEdit && <TableHead></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {schedules.map((schedule: ScheduleDto) => (
                  <TableRow key={schedule.id}>
                    <TableCell>{formatScheduleRecurrence(schedule)}</TableCell>
                    <TableCell>{formatTime(schedule.startTime)}</TableCell>
                    <TableCell>{formatTime(schedule.endTime)}</TableCell>
                    {canEdit && (
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { onDeleteSchedule(schedule); }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
